import {
  ApiError,
  formatMoney,
  type ProductListRow,
  queryKeys,
  type RepairPart,
  type RepairPartsResponse,
} from '@mevaser/shared'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Wrench } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { useDebouncedValue } from './image-library-grid.js'

interface StockLevelRow {
  productId: string
  locationId: string
  locationName: string
  available: number
}

interface UnitRow {
  id: string
  imei: string
  costCents: number | null
  locationName: string | null
}

type AddMode = 'stock' | 'unit' | 'other'

/** The sub-line under a part's label: what it was and where it came from. */
function partDetail(part: RepairPart): string | null {
  if (part.source === 'stock') {
    const where = part.locationName ? ` · ${part.locationName}` : ''
    return `${part.qty} × ${part.productName ?? 'part'}${where}`
  }
  if (part.source === 'unit') return part.unitIdentifier ? `No. ${part.unitIdentifier}` : null
  return [part.vendor, part.invoiceRef ? `Invoice ${part.invoiceRef}` : null]
    .filter(Boolean)
    .join(' · ')
}

/** Everything a repair part can touch: the parts list, stock numbers and units. */
function useInvalidatePartData(ticketId: string) {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.repairs.parts(ticketId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() }),
    ])
  }
}

/**
 * t1237: the parts used on one repair — counted stock, a tracked license or
 * SIM, or a free-text cost. This is the shop's cost; the ticket's quote is not
 * touched.
 */
export function RepairPartsCard({ ticketId, cancelled }: { ticketId: string; cancelled: boolean }) {
  const { can } = useAuth()
  const canWrite = can('repairs:write')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<RepairPart | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.repairs.parts(ticketId),
    queryFn: () => api.get<RepairPartsResponse>(`/repairs/${ticketId}/parts`),
  })
  const parts = data?.items ?? []

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Wrench className="size-4" aria-hidden="true" /> Parts
        </CardTitle>
        {canWrite && !cancelled ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" aria-hidden="true" /> Add part
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-12" />
        ) : parts.length === 0 ? (
          <p className="text-sm text-foreground-muted">No parts recorded yet.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {parts.map((part) => (
              <li key={part.id} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium">{part.label}</p>
                  {partDetail(part) ? (
                    <p className="text-xs text-foreground-muted">{partDetail(part)}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tabular">{formatMoney(part.amountCents)}</span>
                  {canWrite ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${part.label}`}
                      onClick={() => setRemoving(part)}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
            <li className="flex justify-between py-2 font-medium">
              <span>Total cost</span>
              <span className="tabular">{formatMoney(data?.totalCents ?? 0)}</span>
            </li>
          </ul>
        )}
      </CardContent>
      <AddRepairPartDialog ticketId={ticketId} open={adding} onClose={() => setAdding(false)} />
      <RemoveRepairPartDialog
        ticketId={ticketId}
        part={removing}
        onClose={() => setRemoving(null)}
      />
    </Card>
  )
}

function AddRepairPartDialog({
  ticketId,
  open,
  onClose,
}: {
  ticketId: string
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const invalidate = useInvalidatePartData(ticketId)
  const [mode, setMode] = useState<AddMode>('stock')

  // Shared product search for the two inventory modes.
  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)
  const [product, setProduct] = useState<ProductListRow | null>(null)

  const [locationId, setLocationId] = useState('')
  const [qty, setQty] = useState('1')
  const [unit, setUnit] = useState<UnitRow | null>(null)
  const [amount, setAmount] = useState('')
  const [amountTouched, setAmountTouched] = useState(false)

  const [label, setLabel] = useState('')
  const [vendor, setVendor] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')

  const productType = mode === 'stock' ? 'stock' : 'digital_license,sim'
  const products = useQuery({
    queryKey: queryKeys.products.list({ q, type: productType, forRepair: true }),
    queryFn: () =>
      api.get<{ items: ProductListRow[] }>('/products', {
        query: { q, type: productType, isArchived: false, pageSize: 10 },
      }),
    enabled: open && mode !== 'other' && q.length >= 2 && product === null,
  })

  const levels = useQuery({
    queryKey: queryKeys.stock.levels({ productId: product?.id }),
    queryFn: () => api.get<StockLevelRow[]>('/stock', { query: { productId: product?.id } }),
    enabled: open && mode === 'stock' && product !== null,
  })
  const shelves = (levels.data ?? []).filter((l) => l.available > 0)
  const selectedShelf = shelves.find((l) => l.locationId === locationId)

  const units = useQuery({
    queryKey: queryKeys.units.list({ productId: product?.id, status: 'sellable', forRepair: true }),
    queryFn: () =>
      api.get<{ items: UnitRow[] }>('/units', {
        query: { productId: product?.id, status: 'sellable', pageSize: 50, sort: 'createdAt' },
      }),
    enabled: open && mode === 'unit' && product !== null && unit === null,
  })

  const qtyNum = Number.parseInt(qty, 10)
  const qtyValid = Number.isInteger(qtyNum) && qtyNum > 0
  const defaultCents =
    mode === 'stock'
      ? product?.costCents != null && qtyValid
        ? product.costCents * qtyNum
        : null
      : (unit?.costCents ?? product?.costCents ?? null)
  const shownAmount = amountTouched
    ? amount
    : defaultCents !== null
      ? (defaultCents / 100).toFixed(2)
      : ''
  const amountValid = shownAmount.trim() !== '' && Number.isFinite(Number(shownAmount))

  function reset() {
    setSearch('')
    setProduct(null)
    setLocationId('')
    setQty('1')
    setUnit(null)
    setAmount('')
    setAmountTouched(false)
    setLabel('')
    setVendor('')
    setInvoiceRef('')
  }

  function close() {
    reset()
    onClose()
  }

  function changeMode(next: AddMode) {
    reset()
    setMode(next)
  }

  const add = useMutation({
    mutationFn: () => {
      const amountCents = Math.round(Number(shownAmount) * 100)
      if (mode === 'stock') {
        return api.post(`/repairs/${ticketId}/parts`, {
          source: 'stock',
          productId: product?.id,
          locationId,
          qty: qtyNum,
          amountCents,
        })
      }
      if (mode === 'unit') {
        return api.post(`/repairs/${ticketId}/parts`, {
          source: 'unit',
          unitId: unit?.id,
          amountCents,
        })
      }
      return api.post(`/repairs/${ticketId}/parts`, {
        source: 'other',
        label: label.trim(),
        amountCents,
        vendor: vendor.trim() || null,
        invoiceRef: invoiceRef.trim() || null,
      })
    },
    onSuccess: async () => {
      await invalidate()
      toast.success(
        'Part added',
        mode === 'other' ? undefined : 'It was taken out of stock and added to this repair.',
      )
      close()
    },
    onError: (error: Error) =>
      toast.error('Could not add the part', error instanceof ApiError ? error.message : undefined),
  })

  const canSubmit =
    amountValid &&
    (mode === 'stock'
      ? product !== null &&
        locationId !== '' &&
        qtyValid &&
        (selectedShelf ? qtyNum <= selectedShelf.available : true)
      : mode === 'unit'
        ? unit !== null
        : label.trim() !== '')

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a part</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-center gap-1">
            <Button
              variant={mode === 'stock' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => changeMode('stock')}
            >
              From inventory
            </Button>
            <Button
              variant={mode === 'unit' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => changeMode('unit')}
            >
              License / SIM
            </Button>
            <Button
              variant={mode === 'other' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => changeMode('other')}
            >
              Other cost
            </Button>
          </div>

          {mode !== 'other' && product === null ? (
            <div className="space-y-2">
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  mode === 'stock'
                    ? 'Search inventory for a part (e.g. battery)'
                    : 'Search for a license or SIM (e.g. Microsoft Word)'
                }
                aria-label="Search inventory"
              />
              {q.length >= 2 && !products.isFetching && (products.data?.items.length ?? 0) === 0 ? (
                <p className="text-sm text-foreground-muted">No matching products.</p>
              ) : null}
              {(products.data?.items.length ?? 0) > 0 ? (
                <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
                  {products.data?.items.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                        onClick={() => setProduct(p)}
                      >
                        <span>
                          <span className="block font-medium">{p.name}</span>
                          <span className="block font-identifier text-2xs text-foreground-muted">
                            {p.sku}
                          </span>
                        </span>
                        <span className="tabular text-foreground-muted">
                          {p.costCents != null ? formatMoney(p.costCents) : 'no cost set'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {mode !== 'other' && product !== null ? (
            <div className="flex items-center justify-between rounded-md border border-border p-2.5 text-sm">
              <span className="font-medium">{product.name}</span>
              <Button variant="ghost" size="sm" onClick={reset}>
                Change
              </Button>
            </div>
          ) : null}

          {mode === 'stock' && product !== null ? (
            <div className="grid grid-cols-2 gap-2">
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger aria-label="Take from location">
                  <SelectValue placeholder={levels.isLoading ? 'Loading…' : 'Take from…'} />
                </SelectTrigger>
                <SelectContent>
                  {shelves.map((l) => (
                    <SelectItem key={l.locationId} value={l.locationId}>
                      {l.locationName} ({l.available} in stock)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="numeric"
                placeholder="Qty"
                aria-label="Quantity used"
              />
              {shelves.length === 0 && !levels.isLoading ? (
                <p className="col-span-2 text-sm text-foreground-muted">
                  This part is not in stock anywhere.
                </p>
              ) : null}
            </div>
          ) : null}

          {mode === 'unit' && product !== null ? (
            unit === null ? (
              (units.data?.items.length ?? 0) === 0 && !units.isLoading ? (
                <p className="text-sm text-foreground-muted">
                  None of these are available in stock.
                </p>
              ) : (
                <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-surface">
                  {units.data?.items.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                        onClick={() => setUnit(u)}
                      >
                        <span className="font-identifier">{u.imei}</span>
                        <span className="text-foreground-muted">{u.locationName ?? ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <div className="flex items-center justify-between rounded-md border border-border p-2.5 text-sm">
                <span className="font-identifier">{unit.imei}</span>
                <Button variant="ghost" size="sm" onClick={() => setUnit(null)}>
                  Change
                </Button>
              </div>
            )
          ) : null}

          {mode === 'other' ? (
            <div className="space-y-3">
              <Field label="What was it?" required>
                {(props) => (
                  <Input
                    {...props}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Microsoft Office license"
                  />
                )}
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="Vendor (optional)"
                  aria-label="Vendor"
                />
                <Input
                  value={invoiceRef}
                  onChange={(e) => setInvoiceRef(e.target.value)}
                  placeholder="Invoice ref (optional)"
                  aria-label="Invoice reference"
                />
              </div>
            </div>
          ) : null}

          {mode === 'other' || product !== null ? (
            <Field
              label="Cost"
              hint="What the part cost the shop. The customer's quote does not change."
            >
              {(props) => (
                <Input
                  {...props}
                  value={shownAmount}
                  onChange={(e) => {
                    setAmountTouched(true)
                    setAmount(e.target.value)
                  }}
                  inputMode="decimal"
                  placeholder="0.00"
                />
              )}
            </Field>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={add.isPending}
            disabled={!canSubmit}
            onClick={() => add.mutate()}
          >
            Add part
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RemoveRepairPartDialog({
  ticketId,
  part,
  onClose,
}: {
  ticketId: string
  part: RepairPart | null
  onClose: () => void
}) {
  const toast = useToast()
  const invalidate = useInvalidatePartData(ticketId)
  const [restock, setRestock] = useState(true)
  const inStockKind = part?.source === 'stock' || part?.source === 'unit'

  const remove = useMutation({
    mutationFn: () =>
      api.delete(`/repairs/${ticketId}/parts/${part?.id}?restock=${inStockKind && restock}`),
    onSuccess: async () => {
      await invalidate()
      toast.success(inStockKind && restock ? 'Removed — put back in stock' : 'Part removed')
      setRestock(true)
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={part !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove {part?.label}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <p>This takes {part ? formatMoney(part.amountCents) : ''} off the repair's parts cost.</p>
          {inStockKind ? (
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={restock}
                onChange={(e) => setRestock(e.target.checked)}
              />
              <span>
                Put it back in stock
                <span className="block text-xs text-foreground-muted">
                  Leave unticked if it was used up and shouldn't return to the shelf.
                </span>
              </span>
            </label>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
